import { useEffect } from 'react';

// Mirrors legacy v1 `ExitJobEditorModal` (CreateGigOrder/JobEditor/modals)
// — a confirmation popup that appears when the user tries to exit the
// wizard after making progress. The legacy flow used the shared
// <Modal> overlay (Card.Section inside a fullscreen Overlay); v2
// previously rendered this inline at the bottom of the wizard via the
// shared ModalShell div, which made it easy to miss. This component
// renders a true centered overlay with a backdrop so the dialog grabs
// attention the same way v1 did. We deliberately stay on inline styles
// (no @stitches token dependency) to match the rest of the v2 proto
// surface.

const FONT = "'Manrope', sans-serif";
const BACKDROP_COLOR = 'rgba(15, 23, 32, 0.55)';

type ExitWizardModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const ExitWizardModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: ExitWizardModalProps) => {
  // Match v1's Modal behavior: pressing Escape closes (treated as cancel).
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: BACKDROP_COLOR,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Exit job editor"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.18)',
          width: '100%',
          maxWidth: 420,
          padding: '24px 28px',
          fontFamily: FONT,
          color: '#1C1C1C',
        }}
      >
        <h4
          style={{
            fontSize: 18,
            fontWeight: 700,
            margin: '0 0 12px 0',
            fontFamily: FONT,
          }}
        >
          Exit job editor
        </h4>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.45,
            margin: '0 0 20px 0',
            color: '#3B3B3B',
            fontFamily: FONT,
          }}
        >
          Are you sure you want to exit?{' '}
          <strong style={{ color: '#A41B1B' }}>
            Your job creation changes will not be saved.
          </strong>
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #DADADA',
              background: '#fff',
              color: '#1C1C1C',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Keep editing
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#A41B1B',
              color: '#fff',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitWizardModal;
